import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, CheckCircle, ArrowRight, Clock, Bell, TrendingUp, Users, DollarSign, Zap, Star, Award, Phone } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';

const AppointmentBusinesses = () => {
  const { formatPrice } = useCurrency();

  // Scroll to top helper
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const industries = [
    { name: 'Dental Practice', route: '/industries/dental', icon: '🦷' },
    { name: 'Chiropractic', route: '/industries/chiropractor', icon: '💆' },
    { name: 'Physical Therapy', route: '/industries/physical-therapy', icon: '🏃' },
    { name: 'Massage Therapy', route: '/industries/massage-therapy', icon: '💆‍♀️' },
    { name: 'Counseling & Therapy', route: '/industries/counseling', icon: '🧘' },
    { name: 'Veterinary Clinic', route: '/industries/veterinary', icon: '🐾' },
    { name: 'Optometry', route: '/industries/optometry', icon: '👓' },
    { name: 'Acupuncture', route: '/industries/acupuncture', icon: '🎯' },
    { name: 'Medspa & Aesthetics', route: '/industries/medspa-aesthetics', icon: '✨' },
    { name: 'Dermatology', route: '/industries/dermatology', icon: '💉' },
    { name: 'Real Estate Agent', route: '/industries/real-estate-agent', icon: '🏡' },
    { name: 'Insurance Agency', route: '/industries/insurance-agency', icon: '🛡️' },
    { name: 'Personal Trainer', route: '/industries/personal-trainer', icon: '💪' },
    { name: 'Photography', route: '/industries/photography', icon: '📸' }
  ];

  const challenges = [
    { icon: '👻', title: 'High No-Show Rates', desc: 'Lost revenue from missed appointments' },
    { icon: '📅', title: 'Missed Follow-Ups', desc: 'Patients forget to schedule next visit' },
    { icon: '⏰', title: 'Last-Minute Gaps', desc: 'Hard to fill cancellations quickly' },
    { icon: '🔀', title: 'Scheduling Conflicts', desc: 'Double-bookings create chaos' },
    { icon: '🔄', title: 'One-Time Clients', desc: 'Hard to get clients to return' }
  ];

  const solutions = [
    { icon: '📱', title: 'Smart Reminders', desc: 'Reduce no-shows by 40% automatically' },
    { icon: '🔔', title: 'Auto Follow-Up Booking', desc: 'Book next appointment before they leave' },
    { icon: '📋', title: 'Cancellation Waitlist', desc: 'Fill gaps instantly from your waitlist' },
    { icon: '🗓️', title: 'Conflict Prevention', desc: 'Smart scheduling eliminates double-bookings' },
    { icon: '💬', title: 'Nurture Campaigns', desc: 'Automated campaigns bring clients back' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0E163B] via-[#1a2654] to-[#0E163B] text-white py-24 px-4 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'radial-gradient(circle at 20px 20px, white 2px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#FF5A3D] rounded-full opacity-10 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#FF8C42] rounded-full opacity-10 blur-3xl"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            {/* Badge */}
            <div className="inline-flex items-center bg-[#FF5A3D]/20 backdrop-blur-sm border border-[#FF5A3D]/30 px-6 py-3 rounded-full mb-8">
              <Calendar size={20} className="text-[#FF5A3D] mr-2" />
              <span className="text-white font-semibold">14 Appointment Industries</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Appointment-Based<br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42]">
                Growth Platform
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
              Eliminate no-shows, fill your schedule automatically, and turn one-time clients into loyal patients
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Button 
                asChild
                className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-bold text-lg px-10 py-7 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-2xl"
              >
                <Link to="/pricing" onClick={scrollToTop}>
                  Start {formatPrice(5)} Trial
                  <ArrowRight className="ml-2" size={20} />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="border-2 border-white/30 bg-white/10 backdrop-blur-sm text-white hover:bg-white hover:text-[#0E163B] font-semibold text-lg px-10 py-7 rounded-xl transition-all duration-300"
              >
                <Link to="/contact" onClick={scrollToTop}>
                  Talk to Expert
                </Link>
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-300">
              <div className="flex items-center">
                <CheckCircle size={18} className="text-green-400 mr-2" />
                <span>Setup in 5 Minutes</span>
              </div>
              <div className="flex items-center">
                <CheckCircle size={18} className="text-green-400 mr-2" />
                <span>No Long-Term Contract</span>
              </div>
              <div className="flex items-center">
                <CheckCircle size={18} className="text-green-400 mr-2" />
                <span>Cancel Anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              COMMON PROBLEMS
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0E163B] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Challenges Holding You Back
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Sound familiar? These are the daily struggles appointment-based businesses face
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {challenges.map((challenge, index) => (
              <Card key={index} className="border-2 border-gray-200 hover:border-[#FF5A3D] hover:shadow-lg transition-all duration-300 group">
                <CardContent className="p-8">
                  <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">{challenge.icon}</div>
                  <h3 className="text-lg font-bold text-[#0E163B] mb-2">{challenge.title}</h3>
                  <p className="text-gray-600">{challenge.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-green-100 text-green-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              OUR SOLUTIONS
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0E163B] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              How We Solve These Problems
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful automation that transforms how you run your business
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {solutions.map((solution, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#FF5A3D] to-[#FF8C42] rounded-xl flex items-center justify-center text-2xl shadow-lg">
                      {solution.icon}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0E163B] mb-2">{solution.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{solution.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#FF5A3D]/10 text-[#FF5A3D] px-4 py-2 rounded-full text-sm font-semibold mb-4">
              CHOOSE YOUR INDUSTRY
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0E163B] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              14 Appointment-Based Industries
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Pre-built snapshots ready for your specific business
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {industries.map((industry, index) => (
              <Link key={index} to={industry.route} onClick={scrollToTop}>
                <div className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 hover:border-[#FF5A3D] rounded-xl p-6 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl cursor-pointer group">
                  <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-300">{industry.icon}</div>
                  <h3 className="text-base font-bold text-[#0E163B] group-hover:text-[#FF5A3D] transition-colors">
                    {industry.name}
                  </h3>
                  <div className="flex items-center text-[#FF5A3D] font-semibold text-sm mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    View <ArrowRight size={14} className="ml-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#0E163B] via-[#1a2654] to-[#0E163B] text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'radial-gradient(circle at 20px 20px, white 2px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Average Results for Appointment Businesses
            </h2>
            <p className="text-xl text-gray-300">
              Real metrics from businesses using our platform
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-center hover:bg-white/15 transition-all duration-300">
              <div className="bg-gradient-to-br from-[#FF5A3D] to-[#FF8C42] w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Bell size={40} className="text-white" />
              </div>
              <div className="text-5xl font-bold text-white mb-2">40%</div>
              <div className="text-xl text-gray-300">Reduction in No-Shows</div>
              <p className="text-sm text-gray-400 mt-2">Automated reminders keep patients coming</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-center hover:bg-white/15 transition-all duration-300">
              <div className="bg-gradient-to-br from-[#FF5A3D] to-[#FF8C42] w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Calendar size={40} className="text-white" />
              </div>
              <div className="text-5xl font-bold text-white mb-2">95%</div>
              <div className="text-xl text-gray-300">Schedule Utilization</div>
              <p className="text-sm text-gray-400 mt-2">Fill gaps from your waitlist automatically</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-center hover:bg-white/15 transition-all duration-300">
              <div className="bg-gradient-to-br from-[#FF5A3D] to-[#FF8C42] w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <DollarSign size={40} className="text-white" />
              </div>
              <div className="text-5xl font-bold text-white mb-2">75%</div>
              <div className="text-xl text-gray-300">Return for Follow-Ups</div>
              <p className="text-sm text-gray-400 mt-2">Automated nurturing brings them back</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 bg-gradient-to-r from-[#FF5A3D] via-[#FF6B47] to-[#FF8C42] text-white relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Award size={64} className="mx-auto mb-6" />
          <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Ready to Dominate Your Local Market?
          </h2>
          <p className="text-xl md:text-2xl mb-10 opacity-95 leading-relaxed">
            Join hundreds of successful appointment-based businesses using Local Business Heroes
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              asChild
              className="bg-white text-[#FF5A3D] hover:bg-gray-100 font-bold text-xl px-12 py-7 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-2xl"
            >
              <Link to="/pricing" onClick={scrollToTop}>
                Start 14-Day Trial for {formatPrice(5)}
              </Link>
            </Button>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <Star size={16} className="mr-2" />
              <span>Setup in 5 Minutes</span>
            </div>
            <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <CheckCircle size={16} className="mr-2" />
              <span>Credit Card Required</span>
            </div>
            <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <Zap size={16} className="mr-2" />
              <span>Cancel Anytime</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AppointmentBusinesses;